import React from "react";

import { Switch } from "antd";

import {
  FlashContainer,
  useFlashState,
} from "shared/components/FlashAnimation";
import { Checkbox } from "shared/components/Form";
import Tooltip from "shared/components/Tooltip";
import { useConfirmation } from "shared/hooks/useConfirmation";

import { QtyInput, ReadOnlyNumber } from "./NumberControls";
import {
  GridContainer,
  TabBar,
  Tab,
  TabToggleRow,
  TabLabel,
  TabDescription,
  TabPill,
  PillDivider,
  PillDropdown,
  PillDropdownTitle,
  PillDropdownDesc,
  PillDropdownAction,
  GroupSection,
  GroupHeaderBar,
  GroupTitle,
  GroupHelperText,
  DocRow,
  DocNameCell,
  DocName,
  DocControlCell,
  DisabledDash,
  VerifiedBadge,
  RunwayBadge,
  AddCustomRow,
  AddCustomInput,
  AddCustomSubmit,
  DeleteButton,
  ConfigureRunwayLink,
} from "./styled";
import type {
  AttachmentItem,
  ConsolidatedAttachmentGridProps,
  ValidationWarning,
} from "./types";
import { usePersonaInheritance } from "./usePersonaInheritance";
import {
  GROUP_ORDER,
  PAYSTUB_VERO_DEFAULT,
  TAB_DESCRIPTIONS,
  buildRunwayLabel,
  getItemDocKey,
  getTargetApplicantTypes,
  getVeroMinimum,
  isPaystubDoc,
  singularizeAtQtyOne,
  toDocKey,
} from "./utils";

export {
  buildRunwayLabel,
  getInheritedSyncUpdates,
  getOrphanedDocIds,
  getPersonasWithDataDivergence,
  getTargetApplicantTypes,
} from "./utils";
export type { AttachmentItem, Persona, ValidationWarning } from "./types";

export const ConsolidatedAttachmentGrid: React.FC<ConsolidatedAttachmentGridProps> =
  ({
    personas,
    items,
    values,
    onTogglePersona,
    onToggleItem,
    onChangeQuantity,
    onAddCustomDoc,
    onDeleteDoc,
    onValidationChange,
    buildLabel,
    onChangeRunway,
    isRunwayEnabled,
    sectionTitle,
    sectionId,
    disabled,
  }) => {
    const { confirm } = useConfirmation();
    const {
      activePersona,
      activeTab,
      applicantPersona,
      handleCustomize,
      handleResetToApplicant,
      isApplicant,
      isEditable,
      isSynced,
      itemsByTypeAndDocKey,
      overriddenPersonas,
      pendingPersonaOp,
      setActiveTab,
    } = usePersonaInheritance({
      personas,
      items,
      values,
      onToggleItem,
      onChangeQuantity,
      onChangeRunway,
      onAddCustomDoc,
      onDeleteDoc,
    });
    const [pillDropdownOpen, setPillDropdownOpen] = React.useState<
      string | null
    >(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const optimisticToggleValuesRef = React.useRef<Record<string, boolean>>({});

    React.useEffect(() => {
      const snapshot: Record<string, boolean> = {};
      personas.forEach((persona) => {
        snapshot[persona.toggleField] = !!values[persona.toggleField];
      });
      optimisticToggleValuesRef.current = snapshot;
    }, [personas, values]);

    React.useEffect(() => {
      if (!pillDropdownOpen) return;
      const handler = (e: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setPillDropdownOpen(null);
        }
      };
      document.addEventListener("mousedown", handler);
      // eslint-disable-next-line
      return () => {
        document.removeEventListener("mousedown", handler);
      };
    }, [pillDropdownOpen]);
    const personaEnabled = activePersona
      ? !!values[activePersona.toggleField]
      : false;

    const localWarningsByKey = React.useMemo(() => {
      const map: Record<string, ValidationWarning> = {};
      personas.forEach((p) => {
        if (!values[p.toggleField]) return;
        // Inherited personas (non-applicant, not overridden) render the
        // applicant's items in the UI but their own AR rows aren't seeded
        // until they diverge. Validate against whichever row set the UI
        // is actually showing — applicant's when inherited, own when custom.
        const inheritsFromApplicant =
          p.key !== applicantPersona?.key && !overriddenPersonas.has(p.key);
        const applicantType = inheritsFromApplicant
          ? applicantPersona?.applicantType
          : p.applicantType;
        if (applicantType == null) return;
        const hasChecked = items.some(
          (item) => item.applicantType === applicantType && item.checked
        );
        if (!hasChecked) {
          map[p.key] = {
            persona: p.label,
            personaKey: p.key,
            message: `${p.label} is enabled without any document requirements configured.`,
            sectionTitle: sectionTitle || "Document Configuration",
            sectionId: sectionId || "",
          };
        }
      });
      return map;
    }, [
      personas,
      items,
      values,
      sectionTitle,
      sectionId,
      overriddenPersonas,
      applicantPersona,
    ]);

    const warnings = React.useMemo(
      () => Object.values(localWarningsByKey),
      [localWarningsByKey]
    );

    const warningsKey = warnings.map((w) => w.persona).join("|");
    const onValidationChangeRef = React.useRef(onValidationChange);
    onValidationChangeRef.current = onValidationChange;
    React.useEffect(() => {
      onValidationChangeRef.current?.(warnings);
    }, [warningsKey, warnings]);

    // Focus a specific persona tab from the ErrorBadgeButton tooltip.
    React.useEffect(() => {
      if (!sectionId) return;
      const el = document.getElementById(sectionId);
      if (!el) return;
      const handler = (e: Event) => {
        const personaKey = (e as CustomEvent).detail?.personaKey;
        if (personaKey) {
          const idx = personas.findIndex((p) => p.key === personaKey);
          if (idx >= 0) setActiveTab(idx);
        }
      };
      el.addEventListener("focus-error-tab", handler);
      // eslint-disable-next-line
      return () => {
        el.removeEventListener("focus-error-tab", handler);
      };
    }, [sectionId, personas]);

    const createFlash = useFlashState();
    const deleteFlash = useFlashState();
    const [pendingDocName, setPendingDocName] = React.useState("");
    const [customDocName, setCustomDocName] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmitCustomDoc = async () => {
      const trimmed = customDocName.trim();
      if (trimmed && onAddCustomDoc) {
        const targetTypes = getTargetApplicantTypes(
          personas,
          activePersona?.key,
          overriddenPersonas,
          applicantPersona?.key
        );
        setPendingDocName(trimmed);
        createFlash.startFlash("creating");
        setCustomDocName("");
        try {
          await onAddCustomDoc(trimmed, targetTypes);
        } catch {
          // onAddCustomDoc already surfaces an error toast. Swallow the
          // rejection here so the flash/input-focus cleanup still runs
          // without triggering an unhandled rejection.
        } finally {
          createFlash.stopFlash();
          setPendingDocName("");
          inputRef.current?.focus();
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmitCustomDoc();
      } else if (e.key === "Escape") {
        setCustomDocName("");
        inputRef.current?.blur();
      }
    };

    const displayPersona =
      isSynced && applicantPersona ? applicantPersona : activePersona;
    const displayItems = items.filter(
      (item) => item.applicantType === displayPersona?.applicantType
    );

    // Fan-out target list for sync-mode edits. Empty when the active tab
    // is a customized persona (write only the active row's id).
    const inheritingApplicantTypes = React.useMemo(() => {
      if (!applicantPersona) return [];
      if (!isApplicant && !isSynced) return [];
      return personas
        .filter(
          (p) =>
            p.key === applicantPersona.key || !overriddenPersonas.has(p.key)
        )
        .map((p) => p.applicantType);
    }, [applicantPersona, isApplicant, isSynced, overriddenPersonas, personas]);

    const resolveFanOutIds = React.useCallback(
      (docLabel: string, primaryItemId: string | number | undefined) => {
        if (primaryItemId == null) return [];
        if (inheritingApplicantTypes.length === 0) return [primaryItemId];
        const docKey = toDocKey(docLabel);
        const ids = new Set<string | number>();
        ids.add(primaryItemId);
        inheritingApplicantTypes.forEach((applicantType) => {
          const match = itemsByTypeAndDocKey.get(applicantType)?.get(docKey);
          if (match?.id != null) ids.add(match.id);
        });
        return Array.from(ids);
      },
      [inheritingApplicantTypes, itemsByTypeAndDocKey]
    );

    const fanOutToggle = React.useCallback(
      (
        docLabel: string,
        targetItem: AttachmentItem | undefined,
        checked: boolean
      ) => {
        resolveFanOutIds(docLabel, targetItem?.id).forEach((id) =>
          onToggleItem(id, checked)
        );
      },
      [resolveFanOutIds, onToggleItem]
    );

    const fanOutQuantity = React.useCallback(
      (
        docLabel: string,
        targetItem: AttachmentItem | undefined,
        quantity: number | null
      ) => {
        if (!onChangeQuantity) return;
        resolveFanOutIds(docLabel, targetItem?.id).forEach((id) =>
          onChangeQuantity(id, quantity)
        );
      },
      [resolveFanOutIds, onChangeQuantity]
    );

    const fanOutRunway = React.useCallback(
      (
        docLabel: string,
        targetItem: AttachmentItem | undefined,
        months: number | null
      ) => {
        if (!onChangeRunway) return;
        resolveFanOutIds(docLabel, targetItem?.id).forEach((id) =>
          onChangeRunway(id, months)
        );
      },
      [resolveFanOutIds, onChangeRunway]
    );

    const grouped = GROUP_ORDER.reduce<Record<string, AttachmentItem[]>>(
      (acc, group) => {
        const groupItems = displayItems.filter((item) => item.group === group);
        if (groupItems.length > 0) {
          acc[group] = groupItems;
        }
        return acc;
      },
      {}
    );

    const getUniqueDocNames = (groupItems: AttachmentItem[]) => {
      const seen = new Set<string>();
      const unique: string[] = [];
      groupItems.forEach((item) => {
        const key = getItemDocKey(item);
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(item.genericName || item.label);
        }
      });
      return unique;
    };

    const getItemForDisplay = (docGenericName: string) =>
      displayPersona
        ? itemsByTypeAndDocKey
            .get(displayPersona.applicantType)
            ?.get(toDocKey(docGenericName))
        : undefined;

    const getItemForActivePersona = (docGenericName: string) =>
      activePersona
        ? itemsByTypeAndDocKey
            .get(activePersona.applicantType)
            ?.get(toDocKey(docGenericName))
        : undefined;

    const getRowLabel = (
      docLabel: string,
      item: AttachmentItem | undefined
    ) => {
      if (!item || !buildLabel || !item.checked)
        return singularizeAtQtyOne(docLabel);
      const qty = item.requiredCount ?? item.requiredQty ?? 1;
      const runwayMonths =
        isRunwayEnabled && isPaystubDoc(docLabel) ? item.runwayMonths : null;
      const runwayLabel = buildRunwayLabel(runwayMonths);
      if (runwayLabel) return runwayLabel;
      if (
        isRunwayEnabled &&
        isPaystubDoc(docLabel) &&
        item.requiredCount == null &&
        item.requiredQty == null
      ) {
        return buildLabel(item, PAYSTUB_VERO_DEFAULT);
      }
      if (qty <= 1) return singularizeAtQtyOne(docLabel);
      return buildLabel(item, qty);
    };

    if (!items || items.length === 0) return null;

    return (
      <>
        <GridContainer>
          <TabBar>
            {personas.map((persona, idx) => {
              const isActive = idx === activeTab;
              const isPrimaryPersona = persona.key === applicantPersona?.key;
              const enabled = !!values[persona.toggleField];
              const synced =
                !isPrimaryPersona && !overriddenPersonas.has(persona.key);
              return (
                <Tab
                  key={persona.key}
                  active={isActive}
                  onClick={() => setActiveTab(idx)}
                >
                  <TabToggleRow>
                    <Switch
                      checked={enabled}
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        const applyToggle = (
                          field: string,
                          nextValue: boolean
                        ) => {
                          const prevValue =
                            optimisticToggleValuesRef.current[field] ??
                            !!values[field];
                          if (prevValue === nextValue) return;
                          optimisticToggleValuesRef.current[field] = nextValue;
                          onTogglePersona(field, nextValue);
                        };
                        applyToggle(persona.toggleField, checked);
                        if (isPrimaryPersona) {
                          personas.forEach((p) => {
                            if (p.key === applicantPersona?.key) return;
                            if (overriddenPersonas.has(p.key)) return;
                            applyToggle(p.toggleField, checked);
                          });
                        }
                        if (checked && !isPrimaryPersona) {
                          setActiveTab(idx);
                        }
                      }}
                      disabled={disabled}
                      size="small"
                    />
                    <TabLabel active={isActive}>{persona.label}</TabLabel>
                    {localWarningsByKey[persona.key] && (
                      <span
                        style={{
                          fontSize: 18,
                          color: "#DC2626",
                          lineHeight: 1,
                        }}
                      >
                        &#9888;
                      </span>
                    )}
                  </TabToggleRow>
                  <TabDescription>
                    {TAB_DESCRIPTIONS[persona.key]}
                  </TabDescription>
                  <div
                    style={{ position: "relative" }}
                    ref={idx === activeTab ? dropdownRef : undefined}
                  >
                    {isPrimaryPersona ? (
                      <TabPill variant="primary" type="button">
                        Primary
                      </TabPill>
                    ) : (
                      <>
                        <TabPill
                          variant={synced ? "inherited" : "custom"}
                          interactive
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab(idx);
                            setPillDropdownOpen(
                              pillDropdownOpen === persona.key
                                ? null
                                : persona.key
                            );
                          }}
                        >
                          {synced ? "Inherited" : "Custom"}
                          <PillDivider>
                            <svg
                              width={10}
                              height={10}
                              viewBox="0 0 10 10"
                              fill="none"
                              style={{
                                transform:
                                  pillDropdownOpen === persona.key
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                transition: "transform 0.15s",
                              }}
                            >
                              <path
                                d="M2.5 3.75L5 6.25L7.5 3.75"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </PillDivider>
                        </TabPill>
                        {pillDropdownOpen === persona.key && (
                          <PillDropdown>
                            <PillDropdownTitle>
                              {persona.label}
                            </PillDropdownTitle>
                            <PillDropdownDesc>
                              {synced
                                ? "Using same configuration as Applicant."
                                : "Using custom document requirements, decoupled from Applicant."}
                            </PillDropdownDesc>
                            {(() => {
                              const isPending =
                                pendingPersonaOp?.key === persona.key;
                              const pendingLabel = synced
                                ? "Customizing\u2026"
                                : "Restoring\u2026";
                              const idleLabel = synced
                                ? "Customize"
                                : "Reset to Applicant";
                              return (
                                <PillDropdownAction
                                  type="button"
                                  disabled={isPending}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (isPending) return;
                                    try {
                                      if (synced) {
                                        await handleCustomize();
                                      } else {
                                        await handleResetToApplicant();
                                      }
                                    } finally {
                                      setPillDropdownOpen(null);
                                    }
                                  }}
                                >
                                  <span>
                                    {isPending
                                      ? "\u29D7"
                                      : synced
                                      ? "\u270E"
                                      : "\u21A9"}
                                  </span>
                                  {isPending ? pendingLabel : idleLabel}
                                </PillDropdownAction>
                              );
                            })()}
                          </PillDropdown>
                        )}
                      </>
                    )}
                  </div>
                </Tab>
              );
            })}
          </TabBar>

          {warnings.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                backgroundColor: "#FEF2F2",
                borderBottom: "1px solid #FECACA",
                fontSize: 12,
                color: "#DC2626",
              }}
            >
              <span style={{ fontSize: 14, flexShrink: 0 }}>&#9888;</span>
              <span style={{ flex: 1 }}>
                No documents configured for{" "}
                {warnings.map((w) => w.persona).join(", ")}. Enable at least one
                document or disable the toggle.
              </span>
            </div>
          )}

          {!personaEnabled ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: "#9B93B0",
                fontSize: 13,
              }}
            >
              {activePersona?.label} is disabled. Enable the toggle to configure
              document requirements.
            </div>
          ) : (
            <>
              {GROUP_ORDER.map((group) => {
                const groupItems = grouped[group];
                if (!groupItems || groupItems.length === 0) return null;
                const docNames = getUniqueDocNames(groupItems);

                return (
                  <GroupSection key={group}>
                    <GroupHeaderBar>
                      <GroupTitle>{group}</GroupTitle>
                      {group === "Custom" && (
                        <GroupHelperText>
                          Custom document types added by your team
                        </GroupHelperText>
                      )}
                    </GroupHeaderBar>
                    {docNames.map((docLabel) => {
                      const displayItem = getItemForDisplay(docLabel);
                      const sampleItem = groupItems.find(
                        (i) => getItemDocKey(i) === toDocKey(docLabel)
                      );

                      const isDeleting = deleteFlash.isFlashing(docLabel);
                      const isOn = !!displayItem?.checked;
                      const qty =
                        displayItem?.requiredCount ??
                        displayItem?.requiredQty ??
                        1;
                      const rowLabel = getRowLabel(docLabel, displayItem);

                      return (
                        <FlashContainer key={docLabel} loop={isDeleting}>
                          <DocRow>
                            <DocNameCell>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {isEditable &&
                                  !sampleItem?.isDefault &&
                                  onDeleteDoc && (
                                    <DeleteButton
                                      type="button"
                                      onClick={() => {
                                        const ids = personas
                                          .map((p) =>
                                            itemsByTypeAndDocKey
                                              .get(p.applicantType)
                                              ?.get(toDocKey(docLabel))
                                          )
                                          .filter(Boolean)
                                          .map((item) => item!.id);
                                        confirm(
                                          {
                                            title: "Remove Custom Document",
                                            message: `Are you sure you want to remove "${docLabel}" from all applicant types? This cannot be undone.`,
                                            submitButtonLabel: "Remove",
                                          },
                                          () => {
                                            // Row-level flash shows in-flight deletion; close modal immediately.
                                            const runDelete = async () => {
                                              deleteFlash.startFlash(docLabel);
                                              try {
                                                await onDeleteDoc(ids);
                                              } finally {
                                                deleteFlash.stopFlash();
                                              }
                                            };
                                            runDelete();
                                          }
                                        );
                                      }}
                                      title="Remove custom document"
                                    >
                                      &times;
                                    </DeleteButton>
                                  )}
                                <DocName>{rowLabel}</DocName>
                                {sampleItem?.verified && (
                                  <Tooltip title="This document type can result in auto-verified income extraction">
                                    <VerifiedBadge>Verified</VerifiedBadge>
                                  </Tooltip>
                                )}
                                {isRunwayEnabled && isPaystubDoc(docLabel) && (
                                  <Tooltip title="Configure paystubs by runway (months of income history). Captures the applicant's pay frequency and dynamically calculates the number of paystubs required.">
                                    <RunwayBadge>Runway</RunwayBadge>
                                  </Tooltip>
                                )}
                              </div>
                            </DocNameCell>
                            <DocControlCell>
                              {isSynced ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    minHeight: 28,
                                  }}
                                >
                                  {isOn ? (
                                    <>
                                      <Checkbox
                                        id={`sync-${displayItem?.id}`}
                                        checked
                                        onChange={() => {}}
                                        disabled
                                      />
                                      {(() => {
                                        const isPaystubWithRunwayFlag =
                                          isRunwayEnabled &&
                                          isPaystubDoc(docLabel);
                                        if (
                                          isPaystubWithRunwayFlag &&
                                          displayItem?.runwayMonths != null
                                        ) {
                                          return (
                                            <ReadOnlyNumber
                                              prefix="Last"
                                              value={displayItem.runwayMonths}
                                              suffix={
                                                displayItem.runwayMonths === 1
                                                  ? "month"
                                                  : "months"
                                              }
                                            />
                                          );
                                        }
                                        // Runway flag on but unconfigured — show Vero default of 3.
                                        if (isPaystubWithRunwayFlag) {
                                          return (
                                            <ReadOnlyNumber
                                              value={PAYSTUB_VERO_DEFAULT}
                                            />
                                          );
                                        }
                                        return <ReadOnlyNumber value={qty} />;
                                      })()}
                                    </>
                                  ) : (
                                    <DisabledDash>&mdash;</DisabledDash>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <Checkbox
                                    id={`${
                                      isApplicant
                                        ? displayItem?.id
                                        : getItemForActivePersona(docLabel)?.id
                                    }`}
                                    checked={isOn}
                                    onChange={() => {
                                      const targetItem = isApplicant
                                        ? displayItem
                                        : getItemForActivePersona(docLabel);
                                      if (targetItem) {
                                        fanOutToggle(
                                          docLabel,
                                          targetItem,
                                          !isOn
                                        );
                                      }
                                    }}
                                    disabled={disabled}
                                  />
                                  {isOn &&
                                    sampleItem?.editable &&
                                    (() => {
                                      const targetItem = isApplicant
                                        ? displayItem
                                        : getItemForActivePersona(docLabel);
                                      const isPaystubWithRunwayFlag =
                                        isRunwayEnabled &&
                                        isPaystubDoc(docLabel);
                                      const runwayValue =
                                        targetItem?.runwayMonths ?? null;

                                      // Runway flag on — either read-only default with "Configure runway"
                                      // link, or editable runway months with "Use default".
                                      if (isPaystubWithRunwayFlag) {
                                        const setRunway = (
                                          value: number | null
                                        ) => {
                                          if (!targetItem || !onChangeRunway)
                                            return;
                                          fanOutRunway(
                                            docLabel,
                                            targetItem,
                                            value
                                          );
                                        };
                                        // "Use default" must clear BOTH
                                        // runway_months and required_qty.
                                        // Backend resolver keeps using the
                                        // runway-converted required_qty
                                        // otherwise (e.g. 12 from a prior
                                        // biweekly×6 save) even when
                                        // runway_months=null — the legacy
                                        // fallback to LEGACY_DEFAULTS only
                                        // fires when stored_qty is null too.
                                        const clearRunwayAndQty = () => {
                                          if (!targetItem) return;
                                          fanOutRunway(
                                            docLabel,
                                            targetItem,
                                            null
                                          );
                                          fanOutQuantity(
                                            docLabel,
                                            targetItem,
                                            null
                                          );
                                        };

                                        if (runwayValue != null) {
                                          return (
                                            <>
                                              <QtyInput
                                                prefix="Last"
                                                suffix={
                                                  runwayValue === 1
                                                    ? "month"
                                                    : "months"
                                                }
                                                value={runwayValue}
                                                min={1}
                                                max={24}
                                                disabled={disabled}
                                                onChange={(val) =>
                                                  setRunway(val)
                                                }
                                              />
                                              <ConfigureRunwayLink
                                                type="button"
                                                disabled={disabled}
                                                onClick={clearRunwayAndQty}
                                                title="Use the Vero default of 3 most recent paystubs"
                                              >
                                                Use default
                                              </ConfigureRunwayLink>
                                            </>
                                          );
                                        }
                                        return (
                                          <>
                                            <ReadOnlyNumber
                                              preset
                                              value={PAYSTUB_VERO_DEFAULT}
                                            />
                                            <ConfigureRunwayLink
                                              type="button"
                                              disabled={disabled}
                                              onClick={() => setRunway(3)}
                                              title="Configure a dynamic runway based on pay frequency"
                                            >
                                              Configure runway
                                            </ConfigureRunwayLink>
                                          </>
                                        );
                                      }

                                      // Standard editable qty. Financial
                                      // minimums (paystubs>=3, bank>=2) apply
                                      // universally regardless of section.
                                      return (
                                        <QtyInput
                                          value={qty}
                                          min={getVeroMinimum(docLabel)}
                                          max={24}
                                          disabled={disabled}
                                          onChange={(val) => {
                                            if (!targetItem) return;
                                            fanOutQuantity(
                                              docLabel,
                                              targetItem,
                                              val
                                            );
                                          }}
                                        />
                                      );
                                    })()}
                                </>
                              )}
                            </DocControlCell>
                          </DocRow>
                        </FlashContainer>
                      );
                    })}
                  </GroupSection>
                );
              })}

              {createFlash.flashId && (
                <FlashContainer loop>
                  <DocRow>
                    <DocNameCell>
                      <DocName>{pendingDocName}</DocName>
                    </DocNameCell>
                    <DocControlCell />
                  </DocRow>
                </FlashContainer>
              )}

              {onAddCustomDoc && isEditable && !createFlash.flashId && (
                <AddCustomRow>
                  <AddCustomInput
                    ref={inputRef}
                    type="text"
                    placeholder={
                      grouped.Custom
                        ? "Add another custom document..."
                        : "Add custom document..."
                    }
                    value={customDocName}
                    onChange={(e) => setCustomDocName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    maxLength={255}
                    disabled={disabled}
                  />
                  <AddCustomSubmit
                    type="button"
                    onClick={handleSubmitCustomDoc}
                    disabled={disabled || !customDocName.trim()}
                  >
                    Add
                  </AddCustomSubmit>
                </AddCustomRow>
              )}
            </>
          )}
        </GridContainer>
      </>
    );
  };

export default ConsolidatedAttachmentGrid;
